import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, ChevronDown, MapPin, Star } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, isToday, isSameDay, addWeeks, subWeeks } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { getCalendarBookings, getBooking } from '../lib/bookings';
import { getBookingFeedback } from '../lib/feedback';
import BookingInfoModal from '../components/BookingInfoModal';
import DayHeader from '../components/calendar/DayHeader';
import '../components/MobileListView.css';
import CreateMobileBookingModal from '../components/CreateMobileBookingModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { useModalPersistence } from '../utils/modalPersistence';
import { usePermissions } from '../contexts/PermissionContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';

// Define WorkingHours interfaces
interface WorkingHoursShift {
  start: string;
  end: string;
}

interface DayConfig {
  enabled: boolean;
  shifts: WorkingHoursShift[];
  areas?: string[];
  radius: number;
}

interface WorkingHours {
  [key: string]: DayConfig;
}

// Define ResourceBusinessHour interface
interface ResourceBusinessHour {
  resourceId: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}

// Helper function to format date and time into ISO string
const formatDateTime = (date: string, time: string): string => {
  return `${date}T${time}`;
};

export default function MobileCalendar() {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  const { canAccess } = usePermissionCheck();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBookingDetails, setIsLoadingBookingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileResources, setMobileResources] = useState<any[]>([]);
  const [resourceBusinessHours, setResourceBusinessHours] = useState<ResourceBusinessHour[]>([]);
  const [mobileAvailability, setMobileAvailability] = useState<any[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [viewStartDate, setViewStartDate] = useState<Date>(startOfWeek(new Date()));
  const [viewEndDate, setViewEndDate] = useState<Date>(endOfWeek(new Date()));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewType, setViewType] = useState<'week' | 'day'>('day');
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [isBufferModalOpen, setIsBufferModalOpen] = useState(false);
  const [selectedBufferBlock, setSelectedBufferBlock] = useState<any>(null);
  const [isDeleteBufferModalOpen, setIsDeleteBufferModalOpen] = useState(false);
  const [isBufferBlockModalOpen, setIsBufferBlockModalOpen] = useState(false);
  const [isGoogleMapsModalOpen, setIsGoogleMapsModalOpen] = useState(false);
  const [selectedBookingAddress, setSelectedBookingAddress] = useState<string>('');
  const [bookingFeedback, setBookingFeedback] = useState<Map<string, { rating: number; hasText: boolean }>>(new Map());
  
  // Modal persistence hook
  const { getModalState, clearModalState } = useModalPersistence();

  // Get greeting message based on user type
  const getGreetingMessage = () => {
    if (!currentResource) {
      return 'Welcome back, Admin';
    }
    
    if (currentResource.is_admin) {
      return 'Welcome back, Admin';
    }
    
    if (currentResource.first_name) {
      return `Welcome back, ${currentResource.first_name}`;
    }
    
    return 'Welcome back, Admin';
  };

  // Restore modal state on component mount
  useEffect(() => {
    const restoreModalState = async () => {
      const savedState = getModalState();
      if (savedState && savedState.isOpen && savedState.bookingId) {
        try {
          console.log('📱 Restoring modal state from localStorage:', savedState);
          
          // Fetch the fresh booking data
          const booking = await getBooking(savedState.bookingId);
          if (booking) {
            setSelectedBooking(booking);
            setIsBookingModalOpen(true);
            console.log('📱 Modal state restored successfully');
          } else {
            console.warn('📱 Booking not found, clearing saved state');
            clearModalState();
          }
        } catch (error) {
          console.error('📱 Error restoring modal state:', error);
          clearModalState();
        }
      }
    };

    // Only restore on initial mount
    restoreModalState();
  }, []); // Empty dependency array for mount-only effect
  
  // Initialize week start date (Sunday) and end date (Saturday)
  useEffect(() => {
    // Set to beginning of week (Sunday)
    setViewStartDate(startOfWeek(currentDate));
    // Set to end of week (Saturday)
    setViewEndDate(endOfWeek(currentDate));
  }, [currentDate]);
  
  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch mobile resources and availability
  const fetchMobileResources = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      // First get mobile availability records
      const { data: availabilityData, error: availabilityError } = await supabase
        .from('mobile_availability')
        .select('resource_id, working_hours')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_available', true);

      if (availabilityError) throw availabilityError;
      
      if (!availabilityData?.length) {
        setMobileResources([]);
        setResourceBusinessHours([]);
        setMobileAvailability([]);
        return;
      }
      
      // Store the availability data
      setMobileAvailability(availabilityData);

      // Get the resource details
      const resourceIds = availabilityData.map(ma => ma.resource_id);
      const { data: resources, error: resourcesError } = await supabase
        .from('resources')
        .select('id, first_name, last_name')
        .in('id', resourceIds);

      if (resourcesError) throw resourcesError;

      // Format resources for display
      const formattedResources = resources?.map(resource => ({
        id: resource.id,
        title: `${resource.first_name} ${resource.last_name}`.trim()
      })) || [];

      // Filter resources based on "View All Bookings" permission
      const filteredResources = canAccess.viewAllBookings 
        ? formattedResources 
        : formattedResources.filter(resource => resource.id === currentResource?.id);

      console.log(`${canAccess.viewAllBookings ? 'Showing all resources' : `Filtered to current resource: ${currentResource?.first_name} ${currentResource?.last_name}`} (${filteredResources.length} resource${filteredResources.length !== 1 ? 's' : ''})`);

      // Parse working hours from mobile_availability
      const businessHours: ResourceBusinessHour[] = [];
      
      // Filter availability data to match the filtered resources
      const filteredAvailabilityData = canAccess.viewAllBookings 
        ? availabilityData 
        : availabilityData.filter(ma => ma.resource_id === currentResource?.id);
      
      // Process for each resource
      filteredAvailabilityData.forEach(ma => {
        try {
          const workingHours = ma.working_hours as WorkingHours;
          
          // For each day of the week
          ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].forEach((day, dayIndex) => {
            const dayConfig = workingHours[day];
            
            if (dayConfig?.enabled && dayConfig.shifts?.length > 0) {
              // Add each shift
              dayConfig.shifts.forEach(shift => {
                businessHours.push({
                  resourceId: ma.resource_id,
                  startTime: shift.start,
                  endTime: shift.end,
                  daysOfWeek: [dayIndex]
                });
              });
            }
          });
        } catch (error) {
          console.error('Error parsing working hours for resource:', ma.resource_id, error);
        }
      });

      setMobileResources(filteredResources);
      setResourceBusinessHours(businessHours);
      setMobileAvailability(filteredAvailabilityData);
    } catch (error) {
      console.error('Error fetching mobile resources:', error);
      setError('Failed to load mobile resources. Please try again.');
    }
  }, [selectedBusiness?.id, canAccess.viewAllBookings, currentResource?.id]);
  
  // Fetch events/bookings for the current week
  const fetchEvents = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setIsLoading(true);
      
      console.log(`Fetching MOBILE bookings for week: ${viewStartDate.toDateString()} to ${viewEndDate.toDateString()}`);
      
      // Determine if we should filter by resource based on the viewAllBookings permission
      const shouldFilterByResource = !canAccess.viewAllBookings && currentResource?.id;
      const filterResourceId = shouldFilterByResource ? currentResource.id : undefined;
      
      // Fetch only mobile bookings (isLocationBooking = false)
      const events = await getCalendarBookings(
        selectedBusiness.id,
        viewStartDate,
        viewEndDate,
        false, // mobile bookings
        filterResourceId // Pass the resource ID for filtering if needed
      );
      
      console.log(`Fetched ${events.length} mobile bookings${shouldFilterByResource ? ` filtered for resource ${currentResource.first_name} ${currentResource.last_name}` : ' (all bookings)'}`);
      
      // Now also fetch buffer blocks from mobile_bookings table
      if (supabase) {
        const { data: bufferBlocks, error: bufferError } = await supabase
          .from('mobile_bookings')
          .select(`
            id,
            business_id,
            resource_id,
            booking_id,
            start_date,
            start_time,
            end_date,
            end_time,
            buffer_time,
            is_buffer_block
          `)
          .eq('business_id', selectedBusiness.id)
          .eq('is_buffer_block', true)
          .gte('start_date', viewStartDate.toISOString().split('T')[0])
          .lte('end_date', viewEndDate.toISOString().split('T')[0]);
          
        if (bufferError) {
          console.error('Error fetching buffer blocks:', bufferError);
        } else {
          console.log(`Fetched ${bufferBlocks?.length || 0} buffer blocks`);
          
          // Process buffer blocks into CalendarBooking format
          const bufferEvents = (bufferBlocks || []).map(block => {
            // Format dates for display - ensure exact time formatting
            const startDate = new Date(`${block.start_date}T${block.start_time}`);
            const endDate = new Date(`${block.end_date}T${block.end_time}`);
            
            // Make sure there's no duration error by explicitly setting time components
            const start = startDate.toISOString();
            const end = endDate.toISOString();
            
            console.log(`Buffer block time: ${start} to ${end}`);
            
            // Create a buffer block event
            return {
              id: `buffer-${block.id}`, // Prefix to identify as buffer
              title: `Buffer`,
              booking_number: 0, // Buffer blocks don't have booking numbers
              start,
              end,
              backgroundColor: '#000000', // Black for buffer blocks
              borderColor: '#000000',
              secondaryColor: null,
              allDay: false,
              display: 'block',
              resourceId: block.resource_id,
              extendedProps: {
                status: 'buffer',
                is_buffer_block: true,
                buffer_time: block.buffer_time,
                booking_id: block.booking_id,
                resource_id: block.resource_id,
                mobile_booking: true,
                // Minimum required fields to match booking structure
                customer: 'Buffer',
                booking_number: 0,
                total_amount: 0,
                payments: [],
                services: []
              }
            };
          });
          
          // Process events for display (both regular bookings and buffer blocks)
          const processedEvents = [...events, ...bufferEvents].map(event => {
            return {
              ...event,
              display: 'block',
              backgroundColor: event.backgroundColor,
              secondaryColor: event.secondaryColor,
              borderColor: event.backgroundColor
            };
          });
          
          setEvents(processedEvents);
          return;
        }
      }
      
      // If supabase client is not available or if fetching buffer blocks failed
      // Process events without buffer blocks
      const processedEvents = events.map(event => {
        return {
          ...event,
          display: 'block',
          backgroundColor: event.backgroundColor,
          secondaryColor: event.secondaryColor,
          borderColor: event.backgroundColor
        };
      });
      
      setEvents(processedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness?.id, viewStartDate, viewEndDate, canAccess.viewAllBookings, currentResource?.id]);

  // Add a function to load feedback for completed bookings
  const loadFeedbackForBookings = useCallback(async (bookings: any[]) => {
    if (!selectedBusiness?.id) return;
    
    const completedBookings = bookings.filter(booking => booking.extendedProps?.status === 'completed');
    if (completedBookings.length === 0) return;
    
    const feedbackMap = new Map<string, { rating: number; hasText: boolean }>();
    
    // Load feedback for each completed booking
    await Promise.all(
      completedBookings.map(async (booking) => {
        try {
          const feedback = await getBookingFeedback(booking.id);
          if (feedback) {
            // Calculate average rating from form_responses or legacy fields
            let averageRating = 0;
            let ratingCount = 0;
            
            if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
              // Use dynamic form responses
              Object.values(feedback.form_responses).forEach(value => {
                if (typeof value === 'number' && value > 0) {
                  averageRating += value;
                  ratingCount++;
                }
              });
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            } else {
              // Fallback to legacy fields
              if (feedback.overall_quality_rating) {
                averageRating += feedback.overall_quality_rating;
                ratingCount++;
              }
              if (feedback.overall_experience_rating) {
                averageRating += feedback.overall_experience_rating;
                ratingCount++;
              }
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            }
            
            if (averageRating > 0) {
              feedbackMap.set(booking.id, {
                rating: averageRating,
                hasText: !!(feedback.improvement_suggestions || 
                          (feedback.form_responses && Object.values(feedback.form_responses).some(v => typeof v === 'string' && v.trim())))
              });
            }
          }
        } catch (error) {
          console.error('Error loading feedback for booking:', booking.id, error);
        }
      })
    );
    
    setBookingFeedback(feedbackMap);
  }, [selectedBusiness?.id]);
  
  // Load resources and events when component mounts or when dates change
  useEffect(() => {
    fetchMobileResources();
  }, [fetchMobileResources]);
  
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Load feedback when events change
  useEffect(() => {
    if (events.length > 0) {
      loadFeedbackForBookings(events);
    }
  }, [events, loadFeedbackForBookings]);
  
  // Handler for booking click - fetch the full booking details
  const handleBookingClick = async (booking: any) => {
    try {
      setIsLoadingBookingDetails(true);
      
      // Check if this is a buffer block
      if (booking.extendedProps?.is_buffer_block) {
        console.log('Buffer block clicked:', booking);
        
        // Set the selected buffer block and open the modal
        setSelectedBufferBlock({
          id: booking.id,
          buffer_time: booking.extendedProps.buffer_time,
          booking_id: booking.extendedProps.booking_id,
          start: new Date(booking.start),
          end: new Date(booking.end),
          resource_id: booking.extendedProps.resource_id
        });
        setIsBufferModalOpen(true);
        setIsLoadingBookingDetails(false);
        return;
      }
      
      // Fetch full booking details
      const fullBooking = await getBooking(booking.id);
      if (fullBooking) {
        setSelectedBooking(fullBooking);
        setIsBookingModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      setError('Failed to load booking details. Please try again.');
    } finally {
      setIsLoadingBookingDetails(false);
    }
  };
  
  // Navigation handlers - update these to handle day view differently
  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    // If in day view, move back 1 day; otherwise, move back 7 days
    if (viewType === 'day') {
      newDate.setDate(currentDate.getDate() - 1);
    } else {
      newDate.setDate(currentDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };
  
  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    // If in day view, move forward 1 day; otherwise, move forward 7 days
    if (viewType === 'day') {
      newDate.setDate(currentDate.getDate() + 1);
    } else {
      newDate.setDate(currentDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  
  // Helper to format time
  const formatTime = (time: string) => {
    if (!time) return '';
    
    // Format as 12-hour time (h:mm a)
    const [hours, minutes] = time.split(':');
    const parsedHours = parseInt(hours, 10);
    const ampm = parsedHours >= 12 ? 'PM' : 'AM';
    const displayHours = parsedHours % 12 || 12;
    
    return `${displayHours}:${minutes} ${ampm}`;
  };
  
  // Function to handle view change
  const handleViewChange = (view: 'week' | 'day') => {
    setViewType(view);
    setIsViewDropdownOpen(false);
  };
  
  // Function to toggle notes visibility (for list view)
  const toggleNotes = (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedNotes(newExpanded);
  };
  
  // Helper to calculate payment status
  const getPaymentStatus = (event: any) => {
    const totalAmount = event.extendedProps.total_amount || 0;
    const payments = event.extendedProps.payments || [];
    
    // Calculate total paid amount from completed payments
    const amountPaid = payments.reduce((sum: number, payment: any) => 
      payment.status === 'completed' ? sum + payment.amount : sum, 0);
    
    return {
      hasPaid: amountPaid > 0,
      isPaidInFull: amountPaid >= totalAmount
    };
  };
  
  // Add the function to handle buffer block deletion
  const handleDeleteBufferBlock = async () => {
    if (!supabase || !selectedBufferBlock || !selectedBufferBlock.id) {
      console.error('Cannot delete buffer block: Missing data or database connection');
      return;
    }
    
    try {
      setIsLoadingBookingDetails(true);
      
      // Extract the actual ID from the buffer-{id} format
      const bufferId = selectedBufferBlock.id.replace('buffer-', '');
      
      // Delete the buffer block from the mobile_bookings table
      const { error } = await supabase
        .from('mobile_bookings')
        .delete()
        .eq('id', bufferId);
      
      if (error) {
        console.error('Error deleting buffer block:', error);
        throw error;
      }
      
      // Close the modal and refresh events
      setIsBufferModalOpen(false);
      fetchEvents(); // Refresh the calendar to show changes
      
      // Show success message
      alert('Buffer block successfully deleted');
    } catch (error) {
      console.error('Failed to delete buffer block:', error);
      alert('Failed to delete buffer block. Please try again.');
    } finally {
      setIsLoadingBookingDetails(false);
    }
  };
  
  // Function to handle opening Google Maps
  const openGoogleMaps = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const mapsUrl = `https://maps.google.com/?q=${encodedAddress}`;
    
    // Try to open the Google Maps app if on mobile, otherwise open in browser
    if (isMobile) {
      // Try to open Google Maps app first
      const googleMapsApp = `comgooglemaps://?q=${encodedAddress}`;
      const appleMapsApp = `maps://maps.apple.com/?q=${encodedAddress}`;
      
      // Create a hidden link to try opening the app
      const link = document.createElement('a');
      link.href = googleMapsApp;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Try to click the link
      link.click();
      
      // After a short delay, open web version as fallback
      setTimeout(() => {
        window.open(mapsUrl, '_blank');
        document.body.removeChild(link);
      }, 500);
    } else {
      // On desktop, just open in browser
      window.open(mapsUrl, '_blank');
    }
  };

  // Function to handle map pin button click
  const handleMapPinClick = (event: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const address = event.extendedProps?.booking_address;
    if (!address) {
      alert('No address available for this booking');
      return;
    }
    
    setSelectedBookingAddress(address);
    setIsGoogleMapsModalOpen(true);
  };

  // Add helper function to get the service title
  const getServiceTitle = (booking: any) => {
    if (!booking?.extendedProps?.services || booking.extendedProps.services.length === 0) {
      return 'No service';
    }
    
    // Get the first service name
    const firstService = booking.extendedProps.services[0];
    return firstService.name || 'Unnamed service';
  };

  // Helper function to get resource information
  const getResourceInfo = (resourceId: string) => {
    return mobileResources.find(resource => resource.id === resourceId);
  };

  // Helper function to create resource avatar
  const ResourceAvatar = ({ resourceId, size = 'w-6 h-6' }: { resourceId: string; size?: string }) => {
    const resource = getResourceInfo(resourceId);
    
    if (!resource) {
      return (
        <div className={`${size} rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0`}>
          <span className="text-xs font-medium text-white">?</span>
        </div>
      );
    }

    // Create initials from the resource title
    const initials = resource.title.split(' ').map((name: string) => name[0]).join('').substring(0, 2);
    
    return (
      <div className={`${size} rounded-full bg-white/20 flex items-center justify-center flex-shrink-0`}>
        <span className="text-xs font-medium text-white">
          {initials}
        </span>
      </div>
    );
  };
  
  if (error) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm border border-red-100">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-start">
          <div className="flex-shrink-0 mr-3">
            <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium mb-1">Error loading calendar</h3>
            <p className="text-sm">{error}</p>
            <button 
              onClick={() => {
                setError(null);
                fetchEvents();
              }}
              className="mt-2 text-sm text-blue-600 font-medium hover:text-blue-800 transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Calculate if we have any events this week (for empty state)
  const hasEventsThisWeek = events.length > 0;
  
  // Filter events for the current day (for day view)
  const todaysEvents = events.filter(event => {
    const eventDate = new Date(event.start);
    return (
      eventDate.getDate() === currentDate.getDate() &&
      eventDate.getMonth() === currentDate.getMonth() &&
      eventDate.getFullYear() === currentDate.getFullYear()
    );
  // Sort events by start time to ensure proper chronological order
  }).sort((a, b) => {
    const aStart = new Date(a.start).getTime();
    const bStart = new Date(b.start).getTime();
    return aStart - bStart;
  });
  
  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Mobile-optimized header with navigation controls */}
      <header className="bg-white border-b border-gray-200 py-3 px-4 sticky top-0 z-10">
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
              <h1 className="text-lg font-semibold text-gray-900">Mobile Bookings</h1>
            </div>
            
            {/* View selection dropdown */}
            <div className="relative">
              <button 
                className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-md"
                onClick={() => setIsViewDropdownOpen(!isViewDropdownOpen)}
              >
                {viewType === 'week' ? 'Week View' : 'Day View'}
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              
              {isViewDropdownOpen && (
                <div className="absolute right-0 mt-1 w-36 bg-white rounded-md shadow-lg overflow-hidden z-20 border border-gray-200">
                  <div className="py-1">
                    <button 
                      className={`block w-full text-left px-4 py-2 text-sm ${viewType === 'day' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleViewChange('day')}
                    >
                      Day View
                    </button>
                    <button 
                      className={`block w-full text-left px-4 py-2 text-sm ${viewType === 'week' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => handleViewChange('week')}
                    >
                      Week View
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <button 
              onClick={handlePrevWeek}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label={viewType === 'day' ? "Previous Day" : "Previous Week"}
              title={viewType === 'day' ? "Previous Day" : "Previous Week"}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            <button
              onClick={handleToday}
              className="px-3 py-1 text-sm bg-blue-50 text-blue-600 font-medium rounded-full hover:bg-blue-100 transition-colors"
            >
              Today
            </button>
            
            <button
              onClick={handleNextWeek}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label={viewType === 'day' ? "Next Day" : "Next Week"}
              title={viewType === 'day' ? "Next Day" : "Next Week"}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            
            {/* Week range display - moved inline with navigation controls */}
            <span className="ml-3 text-xs text-gray-500 font-medium truncate">
              {viewType === 'day' 
                ? format(currentDate, 'MMM d, yyyy')
                : `${format(viewStartDate, 'MMM d')} - ${format(viewEndDate, 'MMM d, yyyy')}`
              }
            </span>
          </div>
        </div>
      </header>
      
      {/* Add global fixed styles to ensure content is never cut off by the header */}
      <style jsx global>{`
        /* Ensure content is never cut off by the header */
        .mobile-list-view-container {
          padding-top: 70px !important;
          margin-top: 70px !important;
        }
        
        /* Buffer block specific styles */
        .buffer-block-day-view {
          margin-top: 70px !important; /* Extra space for the first item */
        }
      `}</style>
      
      {/* Main content with loading state */}
      <main className="flex-1 overflow-auto pb-16 pt-6 mt-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="relative">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              <div className="animate-spin rounded-full h-10 w-10 border-l-2 border-gray-200 absolute top-0 left-0" style={{ animationDuration: '1.5s' }}></div>
            </div>
            <span className="ml-3 text-sm text-gray-500 font-medium">Loading...</span>
          </div>
        ) : viewType === 'day' ? (
          // Day View - List of bookings for the current day
          <div className="w-full overflow-x-hidden mobile-list-view-container" 
            style={{ 
              height: 'auto',
              minHeight: 'calc(100vh - 200px)',
              maxHeight: 'none',
              position: 'relative',
              zIndex: 5,
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              paddingTop: '1rem',
              marginTop: '1rem',
              paddingBottom: '120px', // Add bottom padding for mobile nav
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
            <style jsx>{`
              @media (min-width: 768px) {
                .mobile-list-view-container {
                  max-width: 700px !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                  border: 1px solid #eaecef;
                  border-radius: 8px;
                }

                .mobile-list-view {
                  border-radius: 8px;
                }
              }
              
              .mobile-list-view {
                height: auto !important;
                min-height: calc(100vh - 250px) !important;
                max-height: none !important;
                overflow-y: visible !important;
                overflow-x: hidden !important;
                padding-top: 1rem !important;
                padding-bottom: 120px !important;
              }
              
              .calendar-list-content {
                height: auto !important;
                min-height: auto !important;
                padding-bottom: 120px !important;
                overflow: visible !important;
              }
              
              .booking-item {
                position: relative;
                z-index: 5;
                background-color: white;
                border-radius: 0.75rem;
                margin-bottom: 1rem;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              }

              /* Desktop optimizations */
              @media (min-width: 768px) {
                .booking-item {
                  max-width: 500px !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                  transition: all 0.2s ease;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }
                
                .booking-item:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
                }
                
                .calendar-list-content {
                  max-width: 650px;
                  margin-left: auto;
                  margin-right: auto;
                }
                
                .date-header {
                  max-width: 650px;
                  margin-left: auto;
                  margin-right: auto;
                }
              }

              /* Mobile optimizations */
              @media (max-width: 640px) {
                .mobile-list-view-container {
                  height: auto !important;
                  min-height: calc(100vh - 180px) !important;
                  max-height: none !important;
                  overflow: visible !important;
                  padding-bottom: 120px !important;
                }
                
                .mobile-list-view {
                  height: auto !important;
                  min-height: calc(100vh - 220px) !important;
                  max-height: none !important;
                  overflow: visible !important;
                  padding-bottom: 120px !important;
                }
                
                .booking-item {
                  margin-bottom: 0.75rem;
                  border-radius: 0.75rem;
                  width: 95% !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                }
                
                .booking-content {
                  padding: 0.75rem !important;
                  min-height: 4.5rem;
                }
                
                .booking-notes {
                  padding: 0.75rem !important;
                }
                
                /* Larger touch targets */
                .notes-toggle {
                  min-height: 2rem;
                  min-width: 2rem;
                }
                
                /* Better spacing for date header */
                .date-header {
                  padding: 0.75rem 1rem !important;
                  margin-top: 1rem !important;
                }
                
                /* Ensure calendar list content can expand */
                .calendar-list-content {
                  height: auto !important;
                  min-height: auto !important;
                  max-height: none !important;
                  overflow: visible !important;
                  padding-bottom: 120px !important;
                }
              }
            `}</style>
            <div className="flex flex-col mobile-list-view bg-white" style={{ 
              zIndex: 5, 
              position: 'relative',
              height: 'auto',
              minHeight: 'calc(100vh - 250px)',
              overflow: 'visible'
            }}>
              {/* Spacer div to ensure content doesn't get cut off by header */}
              <div className="w-full" style={{ height: '60px' }}></div>
              
              {/* Greeting message */}
              <div className="mx-3 my-2 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg" style={{ zIndex: 5 }}>
                <div>
                  <h1 className="text-lg font-bold text-white">
                    {getGreetingMessage()}
                  </h1>
                                      <p className="text-blue-100 text-sm font-medium">
                      Here are your bookings for today
                    </p>
                </div>
              </div>
              
              <div className="flex justify-between items-center p-3 border-b border-gray-100 date-header" style={{ backgroundColor: 'white', zIndex: 5 }}>
                <h2 className="text-base font-medium text-gray-900 flex items-center">
                  {format(currentDate, 'EEEE, MMMM d, yyyy')}
                  {isToday(currentDate) && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                      Today
                    </span>
                  )}
                </h2>
              </div>
              
              {/* List of bookings for the current date */}
              <div className="p-3 space-y-3 w-full overflow-x-hidden calendar-list-content bg-white" style={{ 
                zIndex: 5, 
                paddingBottom: '120px',
                minHeight: 'auto',
                height: 'auto',
                position: 'relative',
                backgroundColor: 'white',
                overflow: 'visible'
              }}>
                {todaysEvents.length > 0 ? (
                  todaysEvents.map((event, index) => {
                    const { hasPaid, isPaidInFull } = getPaymentStatus(event);
                    const status = event.extendedProps.status;
                    const secondaryColor = event.extendedProps.secondaryColor;
                    const isBufferBlock = event.extendedProps?.is_buffer_block === true;
                    
                    // Special styling for buffer blocks
                    if (isBufferBlock) {
                      return (
                        <div 
                          key={`buffer-${index}`} 
                          className="rounded-xl overflow-hidden shadow-sm border border-gray-200 w-full booking-item md:max-w-3xl md:mx-auto"
                          style={{ maxWidth: '100%' }}
                          onClick={() => handleBookingClick(event)}
                        >
                          <div 
                            className="px-4 py-3 flex flex-col cursor-pointer booking-content"
                            style={{ backgroundColor: '#000000' }}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex-1 min-w-0 mr-2 overflow-hidden">
                                <p className="text-sm font-medium truncate text-white">
                                  Buffer
                                </p>
                                <p className="text-sm font-medium truncate text-white text-opacity-90">
                                  {format(new Date(event.start), 'h:mm a')} - {format(new Date(event.end), 'h:mm a')}
                                </p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <p className="text-xs truncate text-white text-opacity-80">
                                    {event.extendedProps.buffer_time} minutes buffer time
                                  </p>
                                  
                                  {/* Resource Avatar for Buffer */}
                                  {event.extendedProps.resource_id && (
                                    <ResourceAvatar resourceId={event.extendedProps.resource_id} size="w-7 h-7" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Create background style - for cancelled or in_progress, use solid color
                    const backgroundStyle = status === 'in_progress' ? {
                      backgroundColor: '#eab308' // Yellow
                    } : status === 'cancelled' ? {
                      backgroundColor: '#fee2e2', // Light red (red-100)
                      borderColor: '#fee2e2' // Match the background color to remove visible border
                    } : secondaryColor ? {
                      background: `linear-gradient(135deg, ${event.backgroundColor} 50%, ${secondaryColor} 50%)`
                    } : {
                      backgroundColor: event.backgroundColor
                    };
                    
                    const startTime = new Date(event.start);
                    const endTime = event.end ? new Date(event.end) : new Date(startTime.getTime() + 60 * 60 * 1000);
                    
                    return (
                      <div 
                        key={index} 
                        className="rounded-xl overflow-hidden shadow-sm border border-gray-200 w-full booking-item md:max-w-3xl md:mx-auto"
                        style={{ maxWidth: '100%' }}
                        onClick={() => handleBookingClick(event)}
                      >
                        <div 
                          className="px-4 py-3 flex flex-col cursor-pointer booking-content"
                          style={backgroundStyle}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex-1 min-w-0 mr-2 overflow-hidden">
                              <div className="flex items-center space-x-2">
                                <p className={`text-sm font-medium truncate ${status === 'cancelled' ? 'text-red-700' : 'text-white'}`}>
                                  #{event.extendedProps.booking_number} {event.title}
                                </p>
                                
                                {/* Feedback Rating for completed bookings */}
                                {status === 'completed' && bookingFeedback.get(event.id) && (
                                  <div className="flex items-center bg-white/20 rounded px-1 py-0.5 flex-shrink-0">
                                    <Star 
                                      size={12} 
                                      className="text-yellow-400 fill-current mr-0.5" 
                                    />
                                    <span className="text-white text-xs font-medium">
                                      {bookingFeedback.get(event.id)!.rating.toFixed(1)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              <p className={`text-sm font-medium truncate ${status === 'cancelled' ? 'text-red-500' : 'text-white text-opacity-90'}`}>
                                {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                              </p>
                              {/* Add customer name */}
                              <p className={`text-xs mt-1 truncate ${status === 'cancelled' ? 'text-red-500' : 'text-white text-opacity-80'}`}>
                                {event.extendedProps.customer || 'No customer info'}
                              </p>
                            </div>
                            
                            <div className="flex items-center flex-shrink-0 space-x-2">
                              {hasPaid && (
                                <div 
                                  className={`h-3 w-3 flex-shrink-0 rounded-full ${
                                    isPaidInFull ? 'bg-green-700' : 'bg-yellow-400'
                                  }`} 
                                  title={isPaidInFull ? 'Paid in full' : 'Partially paid'} 
                                />
                              )}
                              <span className={`text-sm font-medium whitespace-nowrap ${status === 'cancelled' ? 'text-red-700' : 'text-white'}`}>
                                ${event.extendedProps.total_amount?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="mt-2 flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs truncate ${status === 'cancelled' ? 'text-red-500' : 'text-white text-opacity-90'}`}>
                                {event.extendedProps.status === 'scheduled' && '🕒 Scheduled'}
                                {event.extendedProps.status === 'completed' && '✅ Completed'}
                                {event.extendedProps.status === 'in_progress' && '🔄 In Progress'}
                                {event.extendedProps.status === 'cancelled' && '❌ Cancelled'}
                                {event.extendedProps.status === 'created' && '📝 Created'}
                                {event.extendedProps.mobile_booking && ' • Mobile'}
                              </span>
                              
                              {/* Resource Avatar */}
                              {event.extendedProps.resource_id && (
                                <ResourceAvatar resourceId={event.extendedProps.resource_id} size="w-7 h-7" />
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              {/* Map pin button */}
                              <button 
                                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  status === 'cancelled' ? 'bg-red-200 text-red-700' : 'bg-white/20 text-white'
                                }`}
                                onClick={(e) => handleMapPinClick(event, e)}
                                title="Open in Google Maps"
                              >
                                <MapPin 
                                  className="h-4 w-4"
                                />
                              </button>
                              
                              {/* Notes dropdown button */}
                              <button 
                                className={`w-8 h-8 rounded-full flex items-center justify-center notes-toggle ${
                                  status === 'cancelled' ? 'bg-red-200 text-red-700' : 'bg-white/20 text-white'
                                }`}
                                onClick={(e) => toggleNotes(event.id, e)}
                                title={expandedNotes.has(event.id) ? "Hide customer notes" : "Show customer notes"}
                              >
                                <ChevronDown 
                                  className={`h-5 w-5 transition-transform duration-200 ${
                                    expandedNotes.has(event.id) ? 'rotate-180' : ''
                                  }`} 
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Customer Notes Section - shown when expanded */}
                        {expandedNotes.has(event.id) && (
                          <div className="px-4 py-3 text-sm border-t border-gray-200 booking-notes">
                            <div className="font-medium text-gray-900 mb-1">Customer Notes:</div>
                            <p className="text-gray-600 text-sm whitespace-pre-line">
                              {event.extendedProps.customer_notes || 'No customer notes available'}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 px-4 mt-6">
                    <div className="bg-blue-50 rounded-full p-4 mb-4">
                      <CalendarIcon className="h-10 w-10 text-blue-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings today</h3>
                    <p className="text-gray-500 text-center text-base max-w-md">
                      There are no mobile bookings scheduled for today.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : !hasEventsThisWeek && mobileResources.length === 0 ? (
          // Empty state when no resources or events in week view
          <div className="flex flex-col items-center justify-center h-64 px-4">
            <div className="bg-blue-50 rounded-full p-3 mb-3">
              <CalendarIcon className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-1">No mobile bookings</h3>
            <p className="text-gray-500 text-center text-sm max-w-md">
              There are no mobile resources available or no mobile bookings scheduled for this week.
            </p>
          </div>
        ) : (
          // Week View - Days stacked vertically with resources for each day
          <div className="px-0 pb-20 w-full">
            {/* Days of the week */}
            {Array.from({ length: 7 }).map((_, dayIndex) => {
              // Calculate the date for this day
              const dayDate = new Date(viewStartDate);
              dayDate.setDate(viewStartDate.getDate() + dayIndex);
              
              // Format the day header
              const formattedDate = format(dayDate, 'EEEE, MMMM d');
              const todayDate = isToday(dayDate);
              
              // Get resources that are available on this day
              const availableResources = mobileResources.filter(resource => {
                // Check if the resource has availability for this day
                const dayOfWeek = dayDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
                const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                const dayName = dayNames[dayOfWeek];
                
                // Find the availability record and parse working hours
                const availabilityRecord = mobileAvailability.find(ma => ma.resource_id === resource.id);
                if (!availabilityRecord || !availabilityRecord.working_hours) return false;
                
                // Parse the working hours JSON
                let workingHours;
                try {
                  workingHours = typeof availabilityRecord.working_hours === 'string'
                    ? JSON.parse(availabilityRecord.working_hours)
                    : availabilityRecord.working_hours;
                  
                  // Check if this day is enabled and has shifts
                  const dayConfig = workingHours[dayName];
                  return dayConfig?.enabled && dayConfig.shifts && dayConfig.shifts.length > 0;
                } catch (error) {
                  console.error('Error parsing working hours:', error);
                  return false;
                }
              });
              
              // Get all bookings for this day
              const dayBookings = events.filter(event => {
                const eventDate = new Date(event.start);
                return (
                  eventDate.getDate() === dayDate.getDate() &&
                  eventDate.getMonth() === dayDate.getMonth() &&
                  eventDate.getFullYear() === dayDate.getFullYear()
                );
              });

              // Skip days with no resources available
              if (availableResources.length === 0) {
                return (
                  <div 
                    key={dayIndex} 
                    className={`day-section mb-2 bg-white shadow-sm overflow-hidden w-full ${
                      todayDate ? 'border-l-4 border-blue-500' : 'border border-gray-200'
                    }`}
                  >
                    {/* Day header with today highlighting */}
                    <div className={`px-0 py-2 border-b ${
                      todayDate ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-200'
                    }`}>
                      <h3 className={`text-sm font-medium ${todayDate ? 'text-blue-700' : 'text-gray-900'} flex items-center pl-2`}>
                        {format(dayDate, 'EEE, MMM d')}
                        {todayDate && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Today</span>}
                      </h3>
                    </div>
                    
                    <div className="flex flex-col items-center justify-center p-4 bg-gray-50">
                      <div className="text-gray-400 mb-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-xs text-center">No resources available</p>
                    </div>
                  </div>
                );
              }
              
              return (
                <div 
                  key={dayIndex} 
                  className={`day-section mb-2 bg-white shadow-sm overflow-hidden w-full ${
                    todayDate ? 'border-l-4 border-blue-500' : 'border border-gray-200'
                  }`}
                >
                  {/* Day header with today highlighting */}
                  <div className={`px-0 py-2 border-b ${
                    todayDate ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-200'
                  }`}>
                    <h3 className={`text-sm font-medium ${todayDate ? 'text-blue-700' : 'text-gray-900'} flex items-center pl-2`}>
                      {format(dayDate, 'EEE, MMM d')}
                      {todayDate && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Today</span>}
                    </h3>
                  </div>
                  
                  {/* Resources for this day */}
                  <div className="p-0 sm:p-2">
                    <div className="space-y-2 px-0">
                      {availableResources.map(resource => {
                        // Get working hours for this resource and day
                        const dayOfWeek = dayDate.getDay();
                        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                        const dayName = dayNames[dayOfWeek];
                        
                        // Find the availability record and parse working hours
                        const availabilityRecord = mobileAvailability.find(ma => ma.resource_id === resource.id);
                        if (!availabilityRecord || !availabilityRecord.working_hours) return null;
                        
                        let workingHours;
                        let startTime;
                        let endTime;
                        let timeSlots = [];
                        let totalMinutes = 0;
                        let startMinutes = 0;
                        let endMinutes = 0;
                        let resourceBookings = [];
                        
                        try {
                          workingHours = typeof availabilityRecord.working_hours === 'string'
                            ? JSON.parse(availabilityRecord.working_hours)
                            : availabilityRecord.working_hours;
                            
                          const dayConfig = workingHours[dayName];
                          if (!dayConfig?.enabled || !dayConfig.shifts || dayConfig.shifts.length === 0) return null;
                          
                          // Use the first shift for now
                          startTime = dayConfig.shifts[0].start;
                          endTime = dayConfig.shifts[0].end;
                          
                          // Calculate minutes from midnight for start and end time
                          const [startHours, startMins] = startTime.split(':').map(Number);
                          const [endHours, endMins] = endTime.split(':').map(Number);
                          
                          startMinutes = startHours * 60 + startMins;
                          endMinutes = endHours * 60 + endMins;
                          totalMinutes = endMinutes - startMinutes;
                          
                          // Create time slots every 30 minutes with a label for each slot
                          for (let i = 0; i <= totalMinutes; i += 30) {
                            const minutes = startMinutes + i;
                            const hours = Math.floor(minutes / 60);
                            const mins = minutes % 60;
                            
                            // Format time for display
                            const formattedHour = hours % 12 || 12; // Convert to 12-hour format
                            const ampm = hours < 12 ? 'am' : 'pm';
                            const timeLabel = `${formattedHour}${mins === 0 ? '' : ':30'}${ampm}`;
                            
                            timeSlots.push({
                              time: `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`,
                              left: (i / totalMinutes) * 100,
                              label: timeLabel
                            });
                          }
                          
                          // Get bookings for this resource and day
                          resourceBookings = dayBookings.filter(event => 
                            event.extendedProps.resource_id === resource.id
                          );
                        } catch (error) {
                          console.error('Error parsing resource configuration:', error);
                          return null;
                        }
                        
                        const hasBookings = resourceBookings.length > 0;
                        
                        return (
                          <div key={resource.id} className="resource-container bg-gray-50 rounded-lg px-1 py-2 border border-gray-200 w-full">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center">
                                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mr-1.5">
                                  <span className="text-xs font-medium text-gray-600">
                                    {resource.title.split(' ').map((name: string) => name[0]).join('')}
                                  </span>
                                </div>
                                <h4 className="font-medium text-sm text-gray-800">{resource.title}</h4>
                              </div>
                              
                              <div className="flex items-center text-xs text-gray-500">
                                <Clock className="h-3 w-3 mr-0.5" />
                                <span className="text-xs">{formatTime(startTime).replace(":00", "")} - {formatTime(endTime).replace(":00", "")}</span>
                              </div>
                            </div>
                            
                            <div className="relative h-20 pt-6 bg-white rounded overflow-hidden border border-gray-200 w-full">
                              {/* Time indicators */}
                              {timeSlots.map((slot, i) => (
                                <React.Fragment key={i}>
                                  <div 
                                    className="absolute h-full w-px bg-gray-200"
                                    style={{ left: `${slot.left}%` }}
                                  />
                                  {/* Show fewer labels on mobile: only display label for every even slot */}
                                  {(i % 2 === 0 || slot.time.endsWith('00')) && (
                                    <div 
                                      className="absolute top-1 text-[9px] text-gray-500 font-medium"
                                      style={{ 
                                        left: `${slot.left}%`, 
                                        transform: 'translateX(-50%)',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {slot.label}
                                    </div>
                                  )}
                                </React.Fragment>
                              ))}
                              
                              {/* Add custom style specifically for buffer blocks */}
                              <style>
                                {`
                                  /* Ensure buffer blocks end exactly at time markers */
                                  .buffer-block {
                                    /* Use hard right edge instead of padding/margin */
                                    border-right: 1px solid #000000 !important;
                                    /* Prevent border from affecting width calculation */
                                    box-sizing: border-box !important;
                                  }
                                  
                                  /* Responsive adjustments for different screen sizes */
                                  @media (max-width: 480px) {
                                    .buffer-block {
                                      /* Ensure visibility at very small screens */
                                      min-width: 10px !important;
                                    }
                                  }
                                  
                                  @media (min-width: 481px) and (max-width: 768px) {
                                    .buffer-block {
                                      min-width: 20px !important;
                                    }
                                  }
                                `}
                              </style>
                              
                              {/* Bookings */}
                              {resourceBookings.map((booking, i) => {
                                const bookingStart = new Date(booking.start);
                                const bookingEnd = new Date(booking.end);
                                
                                // Calculate booking position
                                const bookingStartMinutes = bookingStart.getHours() * 60 + bookingStart.getMinutes();
                                let bookingEndMinutes = bookingEnd.getHours() * 60 + bookingEnd.getMinutes();
                                
                                // Check if this is a buffer block
                                const isBufferBlock = booking.extendedProps?.is_buffer_block === true;
                                
                                // For buffer blocks, ensure precise time calculations
                                if (isBufferBlock) {
                                  // Get the buffer time in minutes and log for debugging
                                  const bufferTimeMinutes = parseInt(booking.extendedProps.buffer_time);
                                  
                                  console.log(`Buffer block in view: ${booking.id}`);
                                  console.log(`  Start: ${bookingStart}`);
                                  console.log(`  End: ${bookingEnd}`);
                                  console.log(`  Buffer time: ${bufferTimeMinutes} minutes`);
                                  console.log(`  StartMinutes: ${bookingStartMinutes}`);
                                  console.log(`  EndMinutes: ${bookingEndMinutes}`);
                                  
                                  // For buffer blocks, calculate the exact end time based on buffer_time
                                  // This ensures the width is exactly based on the buffer duration
                                  const exactEndMinutes = bookingStartMinutes + bufferTimeMinutes;
                                  console.log(`  Recalculated end minutes: ${exactEndMinutes}`);
                                  
                                  // Recalculate the end percent based on the exact buffer time
                                  bookingEndMinutes = exactEndMinutes;
                                  
                                  // No need for tiny adjustments here - we'll handle precision in the rendering
                                }
                                
                                // Calculate percentage within the timeline
                                let bookingStartPercent = ((bookingStartMinutes - startMinutes) / totalMinutes) * 100;
                                let bookingEndPercent = ((bookingEndMinutes - startMinutes) / totalMinutes) * 100;
                                
                                // Clamp to visible area
                                bookingStartPercent = Math.max(0, Math.min(100, bookingStartPercent));
                                bookingEndPercent = Math.max(0, Math.min(100, bookingEndPercent));
                                
                                // Calculate booking width
                                let bookingWidth = bookingEndPercent - bookingStartPercent;
                                
                                // For buffer blocks, ensure exact width calculation
                                if (isBufferBlock) {
                                  // Instead of a fixed adjustment, use a precise calculation based on buffer time
                                  // This ensures the buffer block has the exact width regardless of screen size
                                  const bufferTimeMinutes = parseInt(booking.extendedProps.buffer_time);
                                  const preciseWidth = (bufferTimeMinutes / totalMinutes) * 100;
                                  
                                  // Use the most precise width calculation available
                                  bookingWidth = preciseWidth;
                                  console.log(`  Precise buffer width: ${bookingWidth}%`);
                                }
                                
                                // Apply minimum width (important for visibility on small screens)
                                bookingWidth = Math.max(bookingWidth, isBufferBlock ? 3 : 25);
                                
                                // Skip rendering if booking is not visible
                                if (bookingWidth <= 0) return null;
                                
                                // Create hover effect styles
                                const hoverStyles = `
                                  .booking-block-${booking.id}:hover {
                                    transform: translateY(-2px);
                                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                                  }
                                `;
                                
                                return (
                                  <React.Fragment key={booking.id}>
                                    <style>{hoverStyles}</style>
                                    <div
                                      className={`booking-block-${booking.id} absolute h-12 top-8 rounded-md overflow-hidden transition-all duration-200 cursor-pointer flex items-center ${isBufferBlock ? 'buffer-block' : ''}`}
                                      style={{
                                        left: `${bookingStartPercent}%`,
                                        width: `${bookingWidth}%`,
                                        backgroundColor: isBufferBlock ? '#000000' : booking.backgroundColor,
                                        color: 'white',
                                        borderLeft: '3px solid',
                                        borderLeftColor: isBufferBlock ? '#000000' : (booking.secondaryColor || booking.backgroundColor),
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                        minWidth: isBufferBlock ? '30px' : '60px', // Adjust minimum width based on type
                                        zIndex: 5, // Add z-index to ensure they appear above other elements
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        boxSizing: 'border-box' // Ensure border is included in width calculations
                                      }}
                                      onClick={() => handleBookingClick(booking)}
                                    >
                                      <div className="px-2 py-1 truncate text-xs font-medium w-full">
                                        {bookingWidth > 10 ? (isBufferBlock ? 'Buffer' : (
                                          <>
                                            #{booking.extendedProps.booking_number} - {booking.title}
                                            {bookingWidth > 30 && (
                                              <div className="text-[9px] font-normal text-white text-opacity-90 truncate mt-0.5">
                                                {getServiceTitle(booking)} - {booking.extendedProps.customer}
                                              </div>
                                            )}
                                          </>
                                        )) : ''}
                                        {bookingWidth > 30 && isBufferBlock && (
                                          <div className="text-[9px] font-normal text-white text-opacity-90 truncate mt-0.5">
                                            {booking.extendedProps.buffer_time} min buffer
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </React.Fragment>
                                );
                              })}
                              
                              {/* Empty state when no bookings */}
                              {!hasBookings && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-xs text-gray-400">No bookings scheduled</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add custom styles for mobile optimization */}
      <style jsx>{`
        @media (max-width: 640px) {
          /* Optimize touch targets */
          button {
            min-height: 36px;
            min-width: 36px;
          }
          
          /* Better scrolling on iOS */
          main {
            -webkit-overflow-scrolling: touch;
          }
          
          /* Ensure resource containers have enough space for touches */
          .resource-container {
            padding-bottom: 8px;
          }
        }
      `}</style>
      
      {/* Booking info modal */}
      {isBookingModalOpen && selectedBooking && (
        <BookingInfoModal
          isOpen={isBookingModalOpen}
          booking={selectedBooking}
          onClose={() => {
            console.log('📱 User manually closed modal, clearing localStorage');
            clearModalState(); // Clear localStorage immediately
            setIsBookingModalOpen(false);
            setSelectedBooking(null);
          }}
          onUpdate={() => {
            fetchEvents(); // Refresh the events but keep the modal open
          }}
        />
      )}

      {/* Buffer Block Modal */}
      {isBufferModalOpen && selectedBufferBlock && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Buffer Block Details
                    </h3>
                    
                    <div className="mt-2 mb-4">
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-medium">Duration:</span> {selectedBufferBlock.buffer_time} minutes
                      </p>
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-medium">Start Time:</span> {selectedBufferBlock.start ? format(selectedBufferBlock.start, 'h:mm a') : 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-medium">End Time:</span> {selectedBufferBlock.end ? format(selectedBufferBlock.end, 'h:mm a') : 'N/A'}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 -ml-4 -mr-4 px-4 py-3 rounded-b-lg">
                      <div className="text-sm text-gray-500 mb-3">
                        Do you want to delete this buffer block?
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteBufferBlock}
                >
                  Delete Buffer
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsBufferModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Maps Modal */}
      {isGoogleMapsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsGoogleMapsModalOpen(false)}></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <MapPin className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                      Open in Google Maps
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-3">
                        Do you want to open this address in Google Maps?
                      </p>
                      <div className="bg-gray-50 p-3 rounded-md">
                        <p className="text-sm font-medium text-gray-700">
                          {selectedBookingAddress}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    openGoogleMaps(selectedBookingAddress);
                    setIsGoogleMapsModalOpen(false);
                  }}
                >
                  Open Google Maps
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsGoogleMapsModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 